import { useState, useEffect, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../services/api-service';
import { fetchTv3PlayAvailability, fetchTmdbLtLocalizedName } from '../services/tv3-play-service';
import MovieHeader from './movie-detail/movie-header';
import StreamingProviders from './movie-detail/streaming-providers';
import MetaTags from './meta-tags';
import { Helmet } from 'react-helmet-async';
import { CONSOLE_MESSAGES, TMDB_IMAGE_URLS } from '../constants';
import { BUTTON_CLASSES } from '../styles';
import { useLanguage } from '../contexts/language-context';
import useGoBack from '../hooks/use-go-back';

// Lazy load non-critical sections
const LazySections = lazy(() => import('./movie-detail/lazy-sections'));

// Loading Header Component
const LoadingHeader = () => {
  const { t } = useLanguage();
  const goBack = useGoBack('/');
  
  return (
  <div className="relative min-h-screen bg-gradient-to-br from-gray-900 via-dark-900 to-gray-800">
    {/* Subtle pattern overlay */}
    <div className="absolute inset-0 opacity-20">
      <div className="w-full h-full" style={{
        backgroundImage: `radial-gradient(circle at 25% 25%, rgba(139, 69, 19, 0.3) 0%, transparent 50%), 
                         radial-gradient(circle at 75% 75%, rgba(30, 58, 138, 0.3) 0%, transparent 50%),
                         radial-gradient(circle at 50% 50%, rgba(75, 85, 99, 0.2) 0%, transparent 50%)`,
        backgroundSize: '100% 100%, 80% 80%, 60% 60%'
      }} />
    </div>
    
    {/* Back Button */}
    <div className="relative z-10 pt-8 pb-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={goBack}
          className="inline-flex items-center space-x-2 text-gray-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg p-2"
          aria-label={t('goBack')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          <span>{t('goBack')}</span>
        </button>
      </div>
    </div>

    {/* Loading Content */}
    <div className="relative z-10 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-start gap-8 lg:gap-12">
          {/* Loading Poster */}
          <div className="flex-shrink-0 mx-auto lg:mx-0">
            <div className="w-80 h-120 rounded-xl shadow-2xl bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 animate-pulse border border-gray-600"></div>
          </div>

          {/* Loading Info */}
          <div className="flex-1 text-center lg:text-left">
            <div className="h-16 lg:h-20 bg-gray-700 rounded-lg animate-pulse mb-4"></div>
            <div className="h-6 bg-gray-700 rounded animate-pulse mb-6 w-3/4"></div>
            <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-6">
              <div className="h-10 w-24 bg-gray-700 rounded animate-pulse"></div>
              <div className="h-10 w-32 bg-gray-700 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);
};

// Error Header Component  
const ErrorHeader = ({ error }) => {
  const { t } = useLanguage();
  const goBack = useGoBack('/');
  
  return (
  <div className="relative min-h-screen bg-gradient-to-br from-gray-900 via-dark-900 to-gray-800">
    {/* Back Button */}
    <div className="relative z-10 pt-8 pb-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={goBack}
          className="inline-flex items-center space-x-2 text-gray-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg p-2"
          aria-label={t('goBack')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          <span>{t('goBack')}</span>
        </button>
      </div>
    </div>

    {/* Error Content */}
    <div className="relative z-10 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-600/20 rounded-xl flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-4">{t('movieNotFound')}</h1>
          <p className="text-gray-400 text-lg mb-8">
            {error?.includes('Item not found') 
              ? t('movieNotFoundDesc')
              : error || t('unknownError')
            }
          </p>
          <div className="space-x-4">
            <button
              onClick={goBack}
              className={BUTTON_CLASSES.PRIMARY}
            >
              {t('goBack')}
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-dark-700 hover:bg-dark-600 text-white px-6 py-3 rounded-lg transition-colors"
            >
              {t('movies')}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);
};

function MovieDetail() {
  const { id } = useParams();
  const [movie, setMovie] = useState(null);
  const [cast, setCast] = useState([]);
  const [videos, setVideos] = useState([]);
  const [similarMovies, setSimilarMovies] = useState([]);
  const [streamingProviders, setStreamingProviders] = useState(null);
  const [externalProviders, setExternalProviders] = useState(null);
  const [hasExternalAvailability, setHasExternalAvailability] = useState(false);
  const [tv3PlayLookupDone, setTv3PlayLookupDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTrailer, setSelectedTrailer] = useState(null);

  // Scroll to top when component mounts or movie ID changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  useEffect(() => {
    let mounted = true;
    let timeoutId = null;

    const fetchMovieDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        setTv3PlayLookupDone(false);

        // Create a timeout promise that rejects after 4 seconds
        const timeoutPromise = new Promise((_, reject) => {
          timeoutId = setTimeout(() => {
            reject(new Error('Request timeout'));
          }, 4000);
        });

        // Race between the API call and the timeout
        const dataPromise = apiService.getMovieDetailsComplete(id);
        
        let data;
        try {
          data = await Promise.race([dataPromise, timeoutPromise]);
          // Clear timeout if request succeeded
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
        } catch (timeoutError) {
          // If timeout occurs, wait a bit more for the actual request to potentially complete
          // This gives us partial data if the main API succeeds but external providers are slow
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          
          console.warn('Primary request timeout, waiting for response...', timeoutError.message);
          
          // Wait up to 1 more second for the actual request
          try {
            data = await Promise.race([
              dataPromise,
              new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Final timeout')), 1000)
              )
            ]);
          } catch (finalError) {
            // If still no response, throw the original error
            throw timeoutError;
          }
        }

        if (!mounted) return;

        // Set movie details
        if (data?.details) {
          setMovie(data.details);
        }

        // Set cast (limited to 12)
        if (data?.credits?.cast) {
          setCast(data.credits.cast.slice(0, 12));
        }

        // Set videos (only trailers)
        if (data?.videos?.results) {
          const trailers = data.videos.results.filter(video => video.type === 'Trailer');
          setVideos(trailers);
          // Set first trailer as selected
          if (trailers.length > 0) {
            setSelectedTrailer(trailers[0]);
          }
        }

        // Set similar movies (limited to 8)
        if (data?.similar?.results) {
          setSimilarMovies(data.similar.results.slice(0, 8));
        }

        // Set streaming providers for Lithuania (TMDB)
        let lithuanianProviders = {};
        if (data?.providers?.results) {
          lithuanianProviders = data.providers.results.LT || {};
        }

        // Check external providers (e.g., Telia Play, Go3, Forum Cinemas, Cinamon)
        // Handle providers gracefully - if some fail, just skip them
        let externalAvailable = false;
        let externalProvidersData = {};
        if (data?.providers?.external) {
          // Validate Forum Cinemas matches to prevent incorrect movie associations
          const validatedExternalProviders = { ...data.providers.external };
          
          if (data.details) {
            const movieYear = data.details.release_date?.substring(0, 4);
            const movieId = data.details.id;
            
            // Filter out Forum Cinemas for Zootopia (2016) - only Zootopia 2 should show Forum Cinemas
            // Zootopia (2016) has TMDB ID 269149
            if (movieId === 269149 && movieYear === '2016') {
              if (validatedExternalProviders.forumCinemas?.exists) {
                validatedExternalProviders.forumCinemas = {
                  ...validatedExternalProviders.forumCinemas,
                  exists: false
                };
              }
            }
          }
          
          // Filter out providers that have errors or failed to load
          Object.entries(validatedExternalProviders).forEach(([key, ext]) => {
            if (ext?.exists && !ext.error) {
              externalAvailable = true;
            }
          });
          externalProvidersData = validatedExternalProviders;
        }

        // Store provider data
        setStreamingProviders(lithuanianProviders);
        setExternalProviders(externalProvidersData);
        setHasExternalAvailability(externalAvailable);

        if (data?.details && mounted) {
          try {
            let tv3DisplayTitle = data.details.title;
            try {
              const ltFromTmdb = await fetchTmdbLtLocalizedName(id, 'movie');
              if (ltFromTmdb) tv3DisplayTitle = ltFromTmdb;
              else {
                const ltDetails = await apiService.getMovieDetails(id, 'lt');
                const ltTitle = ltDetails?.title?.trim();
                if (ltTitle) tv3DisplayTitle = ltTitle;
              }
            } catch {
              // optional
            }
            const tv3 = await fetchTv3PlayAvailability({
              title: tv3DisplayTitle,
              originalTitle: data.details.original_title || data.details.title,
              overview: data.details.overview,
            });
            if (mounted && tv3.exists) {
              setExternalProviders((prev) => ({
                ...prev,
                tv3Play: {
                  exists: true,
                  error: false,
                  programDetails: tv3.programDetails,
                },
              }));
              setHasExternalAvailability(true);
            }
          } catch {
            // TV3 Play is optional
          }
        }
      } catch (err) {
        if (mounted) {
          const errorMessage = err?.message?.toLowerCase() || '';
          const isTimeout = errorMessage.includes('timeout');
          
          if (isTimeout) {
            console.warn('Request timed out - movie data may be incomplete', err);
            // For timeout, just show error message, data will be whatever loaded
            setError('Some data may be unavailable due to slow response');
          } else {
            setError(err.message);
            console.error(CONSOLE_MESSAGES.ERROR_FETCHING_MOVIE, err);
          }
        }
      } finally {
        // Always clear timeout and set loading to false
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (mounted) {
          setLoading(false);
          setTv3PlayLookupDone(true);
        }
      }
    };

    fetchMovieDetails();

    return () => {
      mounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [id]);

  function getMovieSchema(movieData) {
    if (!movieData) return {};

    const {
      title,
      overview,
      poster_path,
      backdrop_path,
      release_date,
      vote_average,
      vote_count,
      genres,
      id,
    } = movieData;

    const imageUrl = backdrop_path
      ? `${TMDB_IMAGE_URLS.W1280}${backdrop_path}`
      : `${TMDB_IMAGE_URLS.W500}${poster_path}`;

    return {
      '@context': 'https://schema.org',
      '@type': 'Movie',
      name: title,
      image: imageUrl,
      description: overview,
      datePublished: release_date,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: vote_average,
        ratingCount: vote_count,
      },
      genre: genres?.map((g) => g.name),
      url: `https://filmudb.lt/movie/${id}`,
    };
  }

  // Show loading header while loading
  if (loading) {
    return <LoadingHeader />;
  }

  // Show error header if there's an error
  if (error) {
    return <ErrorHeader error={error} />;
  }

  // Show error header if no movie data
  if (!movie) {
    return <ErrorHeader error="Filmas nerastas" />;
  }

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Structured Data for SEO */}
      {movie && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(getMovieSchema(movie))}
          </script>
        </Helmet>
      )}
      <MetaTags
        title={`${movie.title} (${movie.release_date?.split('-')[0]}) - FilmųDB`}
        description={movie.overview || `Žiūrėkite ${movie.title} - ${movie.genres?.map(g => g.name).join(', ')}. Filmo informacija, aktoriai, anonsas ir kur žiūrėti Lietuvoje.`}
        image={movie.backdrop_path ? `${TMDB_IMAGE_URLS.W1280}${movie.backdrop_path}` : `${TMDB_IMAGE_URLS.W500}${movie.poster_path}`}
        url={`https://filmudb.lt/movie/${movie.id}`}
        type="video.movie"
        keywords={`${movie.title}, filmas, ${movie.genres?.map(g => g.name).join(', ')}, ${movie.release_date?.split('-')[0]}, kinas, anonsas`}
      />
      {/* Movie Header - Critical above-the-fold content */}
      <MovieHeader 
        movie={movie} 
        streamingProviders={streamingProviders}
        hasExternalAvailability={hasExternalAvailability}
        streamingLookupResolved={tv3PlayLookupDone}
      />

      {/* Content Sections - Loaded progressively */}
      <div className="bg-dark-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          
          {/* Streaming Providers - Important for user decisions */}
          <StreamingProviders
            streamingProviders={streamingProviders}
            externalProviders={externalProviders}
            hasExternalAvailability={hasExternalAvailability}
          />
          
          {/* Lazy-loaded sections for better performance */}
          <Suspense 
            fallback={
              <div className="space-y-16">
                <div className="h-64 bg-dark-800 rounded-xl animate-pulse" />
                <div className="h-48 bg-dark-800 rounded-xl animate-pulse" />
                <div className="h-80 bg-dark-800 rounded-xl animate-pulse" />
              </div>
            }
          >
            <LazySections
              movie={movie}
              cast={cast}
              videos={videos}
              selectedTrailer={selectedTrailer}
              setSelectedTrailer={setSelectedTrailer}
              similarMovies={similarMovies}
            />
          </Suspense>
        </div>
      </div>
    </div>
  );
}

export default MovieDetail; 